import React, { useState } from 'react';

interface SimpleRichEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const SimpleRichEditor: React.FC<SimpleRichEditorProps> = ({
  value,
  onChange,
  placeholder = "Enter article text...",
  className = ""
}) => {
  const [uploadingImage, setUploadingImage] = useState(false);

  const insertText = (before: string, after: string = '') => {
    const textarea = document.getElementById('content-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end);
    
    const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
    onChange(newText);
    
    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };

  // Handle image upload for inline content
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, alignment: 'left' | 'center' | 'right' = 'center') => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      console.log('Uploading file:', file.name, 'Size:', file.size); // Debugging

      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      console.log('🔐 Using httpOnly cookies for authentication');
      
      const response = await fetch(`${apiUrl}/api/upload/blog-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      console.log('Response status:', response.status); // Debugging
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Upload response:', result); // Debugging

      if (result.success) {
        // Use direct URL instead of proxy
        const directUrl = `${apiUrl}${result.url}`;
        console.log('Generated direct URL:', directUrl); // Debugging
        
        // Create markdown with alignment
        let imageMarkdown = `![Image](${directUrl})`;
        
        // Add alignment wrapper if not center
        if (alignment === 'left') {
          imageMarkdown = `\n<div style="text-align: left;">\n${imageMarkdown}\n</div>\n`;
        } else if (alignment === 'right') {
          imageMarkdown = `\n<div style="text-align: right;">\n${imageMarkdown}\n</div>\n`;
        } else {
          imageMarkdown = `\n<div style="text-align: center;">\n${imageMarkdown}\n</div>\n`;
        }
        
        console.log('Generated markdown:', imageMarkdown); // Debugging
        
        // Insert the image at cursor position
        insertText(imageMarkdown);
        
        alert('Image uploaded and inserted successfully!');
      } else {
        console.error('Upload failed:', result); // Debugging
        alert(`Upload failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image');
    } finally {
      setUploadingImage(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  const toolbarButtons = [
    { 
      icon: <svg className="w-4 h-4 font-bold" fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h2a4 4 0 014 4 3 3 0 01-3 3 4 4 0 014 4 4 4 0 01-4 4H6V4zm2 5h1a2 2 0 000-4H8v4zm0 2v4h2a2 2 0 000-4H8z"/></svg>,
      title: 'Bold', 
      action: () => insertText('**', '**') 
    },
    { 
      icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4h-8z"/></svg>,
      title: 'Italic', 
      action: () => insertText('*', '*') 
    },
    { 
      icon: <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg>,
      title: 'Heading 1', 
      action: () => insertText('# ') 
    },
    { 
      icon: <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h11" /></svg>,
      title: 'Heading 2', 
      action: () => insertText('## ') 
    },
    { 
      icon: <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>,
      title: 'Heading 3', 
      action: () => insertText('### ') 
    },
    { 
      icon: <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>,
      title: 'List', 
      action: () => insertText('\n- ') 
    },
    { 
      icon: <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>,
      title: 'Numbered List', 
      action: () => insertText('\n1. ') 
    },
    { 
      icon: <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>,
      title: 'Quote', 
      action: () => insertText('\n> ') 
    },
    { 
      icon: <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>,
      title: 'Code', 
      action: () => insertText('`', '`') 
    },
    { 
      icon: <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>,
      title: 'Link', 
      action: () => insertText('[', '](url)') 
    },
  ];

  const alignmentButtons = [
    { 
      icon: <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8M4 18h16" /></svg>,
      title: 'Align Left', 
      action: () => insertText('\n<div style="text-align: left;">\n', '\n</div>\n') 
    },
    { 
      icon: <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M8 12h8M4 18h16" /></svg>,
      title: 'Align Center', 
      action: () => insertText('\n<div style="text-align: center;">\n', '\n</div>\n') 
    },
    { 
      icon: <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M12 12h8M4 18h16" /></svg>,
      title: 'Align Right', 
      action: () => insertText('\n<div style="text-align: right;">\n', '\n</div>\n') 
    },
  ];

  return (
    <div className={`simple-rich-editor ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-[rgba(243,235,226,0.04)] border border-gray-600 rounded-t-lg">
        {/* Text formatting buttons */}
        {toolbarButtons.map((button, index) => (
          <button
            key={index}
            type="button"
            onClick={button.action}
            title={button.title}
            className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded transition-colors flex items-center justify-center"
          >
            {button.icon}
          </button>
        ))}
        
        {/* Separator */}
        <div className="w-px bg-gray-600 mx-1"></div>
        
        {/* Text alignment buttons */}
        {alignmentButtons.map((button, index) => (
          <button
            key={`align-${index}`}
            type="button"
            onClick={button.action}
            title={button.title}
            className="p-2 bg-green-700 hover:bg-green-600 text-white rounded transition-colors flex items-center justify-center"
          >
            {button.icon}
          </button>
        ))}
        
        {/* Separator */}
        <div className="w-px bg-gray-600 mx-1"></div>
        
        {/* Image upload buttons */}
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, 'left')}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            id="image-left-upload"
            disabled={uploadingImage}
          />
          <label
            htmlFor="image-left-upload"
            className={`flex items-center gap-1 p-2 bg-indigo-700 hover:bg-indigo-600 text-white rounded transition-colors cursor-pointer ${
              uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="Add Image (Left)"
          >
            <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <svg className="w-3 h-3" stroke="currentColor" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </label>
        </div>
        
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, 'center')}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            id="image-center-upload"
            disabled={uploadingImage}
          />
          <label
            htmlFor="image-center-upload"
            className={`flex items-center justify-center p-2 bg-indigo-700 hover:bg-indigo-600 text-white rounded transition-colors cursor-pointer ${
              uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="Add Image (Center)"
          >
            <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </label>
        </div>
        
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, 'right')}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            id="image-right-upload"
            disabled={uploadingImage}
          />
          <label
            htmlFor="image-right-upload"
            className={`flex items-center gap-1 p-2 bg-indigo-700 hover:bg-indigo-600 text-white rounded transition-colors cursor-pointer ${
              uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="Add Image (Right)"
          >
            <svg className="w-3 h-3" stroke="currentColor" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </label>
        </div>
        
        {uploadingImage && (
          <div className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-400">
            <div className="animate-spin w-3 h-3 border border-indigo-400 border-t-transparent rounded-full"></div>
            Uploading...
          </div>
        )}
      </div>
      
      {/* Editor */}
      <textarea
        id="content-editor"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={12}
        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 border-t-0 rounded-b-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm leading-relaxed"
      />
      
      {/* Help text */}
      <div className="mt-2 text-xs text-gray-400">
        <div className="flex flex-wrap gap-4">
          <span>**bold** or *italic*</span>
          <span># Heading</span>
          <span>- list</span>
          <span>&gt; quote</span>
          <span>`code`</span>
          <span>[link](url)</span>
          <span className="text-green-400">Text alignment buttons</span>
          <span className="text-indigo-400">Image upload with alignment buttons</span>
        </div>
      </div>
    </div>
  );
};

export default SimpleRichEditor;
